import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useKeyboardLift } from '../hooks/useKeyboardLift';
import { createPortal } from 'react-dom';
import {
  IonPage,
  IonContent,
  IonButton,
  IonIcon,
  IonSkeletonText,
  IonText,
  IonRefresherContent,
  IonGrid,
  IonCard,
  IonCardContent,
  IonSearchbar,
  IonBadge,
  IonLabel,
  IonModal,
  IonRow,
  IonCol,
  IonList,
  IonItem,
  IonAvatar,
  IonFab,
  useIonRouter,
} from '@ionic/react';
import {
  addOutline,
  calendarOutline,
  cashOutline,
  chevronBack,
  chevronDownOutline,
  close,
  documentTextOutline,
  eyeOutline,
  mailOpenOutline,
  mailOutline,
  refreshOutline,
  search as searchIcon,
  swapVertical,
  receiptOutline,
} from 'ionicons/icons';
import BasePageWithHeader, { TitleCard } from '../components/BasePageWithHeader';
import PositionedRefresher from '../components/PositionedRefresher';
import { useScrollSnap } from '../hooks/useScrollSnap';
import CreateInvoice from './CreateInvoice';
import Invoice from './Invoice';
import { applySearchOverlayPosition } from './bookingSystem/overlayPosition';
import { getZohoEstimates, type ZohoEstimate } from '../services/zohoInvoicingService';
import { getDefaultAvatar } from '@rsmaids/shared/utils/avatarUtils';
import styles from './EstimateList.module.scss';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { translateDynamic } from '../i18n/dynamic';


interface EstimateListProps {
  onClose?: () => void;
}

type StatusFilter = 'all' | 'draft' | 'sent' | 'accepted' | 'declined' | 'expired' | 'invoiced';
type SortField = 'date' | 'expiry' | 'amount' | 'estimateNumber' | 'customer';
type SortDirection = 'asc' | 'desc';

const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', get label() { return i18n.t('pages.EstimateList.allEstimates'); } },
  { value: 'draft', get label() { return i18n.t('pages.EstimateList.draft'); } },
  { value: 'sent', get label() { return i18n.t('pages.EstimateList.sent'); } },
  { value: 'accepted', get label() { return i18n.t('pages.EstimateList.accepted'); } },
  { value: 'declined', get label() { return i18n.t('pages.EstimateList.declined'); } },
  { value: 'expired', get label() { return i18n.t('pages.EstimateList.expired'); } },
  { value: 'invoiced', get label() { return i18n.t('pages.EstimateList.invoiced'); } },
];

const SORT_OPTIONS: Array<{ value: SortField; label: string }> = [
  { value: 'date', get label() { return i18n.t('pages.EstimateList.dateIssued'); } },
  { value: 'expiry', get label() { return i18n.t('pages.EstimateList.expiryDate'); } },
  { value: 'amount', get label() { return i18n.t('pages.EstimateList.amount'); } },
  { value: 'estimateNumber', get label() { return i18n.t('pages.EstimateList.estimateNumber'); } },
  { value: 'customer', get label() { return i18n.t('pages.EstimateList.customerName'); } },
];

type PortalDropdownRect = { top: number; left?: number; right?: number; width?: number };

function usePortalDropdownPosition(
  isOpen: boolean,
  anchorRef: React.RefObject<HTMLElement | null>,
  portalRef: React.RefObject<HTMLElement | null>,
  cardRef: React.RefObject<HTMLElement | null>,
  onClose: () => void,
  align: 'start' | 'end' = 'start',
) {
  const [rect, setRect] = useState<PortalDropdownRect | null>(null);
  // Clear the computed rect the moment the dropdown closes — computed
  // during render rather than as a synchronous setState call at the top of
  // the effect below.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (!isOpen && rect !== null) setRect(null);
  }

  useEffect(() => {
    if (!isOpen) return;

    const computeRect = (): PortalDropdownRect | null => {
      const field = anchorRef.current;
      const portal = portalRef.current;
      if (!field || !portal) return null;
      const box = field.getBoundingClientRect();
      const anchor = portal.getBoundingClientRect();
      const top = box.bottom - anchor.top + 4;

      if (align === 'end') {
        return { top, right: anchor.right - box.right };
      }

      return { top, left: box.left - anchor.left, width: box.width };
    };

    const applyRect = (r: PortalDropdownRect) => {
      const card = cardRef.current;
      if (!card) return;
      card.style.top = `${r.top}px`;
      card.style.left = r.left !== undefined ? `${r.left}px` : '';
      card.style.right = r.right !== undefined ? `${r.right}px` : '';
      card.style.width = r.width !== undefined ? `${r.width}px` : '';
    };

    const initial = computeRect();
    setRect(initial);

    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const r = computeRect();
        if (r) {
          setRect(r);
          applyRect(r);
        }
      });
    });

    const handleScrollPosition = () => {
      const r = computeRect();
      if (r) applyRect(r);
    };

    const handleResize = () => {
      const r = computeRect();
      if (r) {
        setRect(r);
        applyRect(r);
      }
    };

    const handleOutsidePointer = (event: PointerEvent) => {
      const target = event.target as Node;
      if (anchorRef.current?.contains(target)) return;
      if (portalRef.current?.contains(target)) return;
      onClose();
    };

    document.addEventListener('scroll', handleScrollPosition, true);
    window.addEventListener('resize', handleResize);
    document.addEventListener('pointerdown', handleOutsidePointer);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      document.removeEventListener('scroll', handleScrollPosition, true);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('pointerdown', handleOutsidePointer);
    };
  }, [isOpen, anchorRef, portalRef, cardRef, onClose, align]);

  return rect;
}

const PortalDropdown: React.FC<{
  isOpen: boolean;
  anchorRef: React.RefObject<HTMLElement | null>;
  portalRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  options: Array<{ label: string; value: string; sublabel?: string }>;
  value?: string;
  onSelect: (value: string) => void;
  align?: 'start' | 'end';
}> = ({ isOpen, anchorRef, portalRef, onClose, options, value, onSelect, align = 'start' }) => {
  const cardRef = useRef<HTMLIonCardElement>(null);
  const rect = usePortalDropdownPosition(isOpen, anchorRef, portalRef, cardRef, onClose, align);
  const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setPortalNode(portalRef.current);
  }, [portalRef, isOpen]);

  if (!isOpen || !rect || !portalNode) return null;

  return createPortal(
    <IonCard ref={cardRef} className={styles.customDropdownList} style={{ ...rect, position: 'absolute' }}>
      <IonList lines="none" className={styles.customDropdownListInner}>
        {options.map((option) => (
          <IonItem
            key={option.value}
            button
            lines="none"
            detail={false}
            className={`${styles.customDropdownItem} ${option.value === value ? styles.customDropdownItemActive : ''}`}
            onClick={() => onSelect(option.value)}
          >
            <IonLabel>{option.label}</IonLabel>
            {option.sublabel && (
              <IonText slot="end" className={styles.customDropdownSublabel}>
                {option.sublabel}
              </IonText>
            )}
          </IonItem>
        ))}
      </IonList>
    </IonCard>,
    portalNode,
  );
};

function formatCurrency(value: number, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(value || 0);
}

function formatDate(value?: string) {
  if (!value) return translateDynamic('Unknown');
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

function getStatusTone(status: string) {
  switch (status.toLowerCase()) {
    case 'accepted':
    case 'invoiced':
      return styles.badgeSuccess;
    case 'sent':
      return styles.badgePrimary;
    case 'declined':
    case 'expired':
      return styles.badgeDanger;
    default:
      return styles.badgeMedium;
  }
}

function getViewedState(estimate: ZohoEstimate): { icon: string; label: string; color: string } | null {
  if (
    String(estimate.status || '').toLowerCase() === 'viewed'
    || estimate.is_viewed_by_client
    || !!estimate.client_viewed_time
    || String(estimate.current_sub_status || '').toLowerCase() === 'viewed'
  ) {
    return { icon: eyeOutline, label: 'Viewed', color: '#f0a500' };
  }

  if (estimate.is_viewed_in_mail || !!estimate.mail_first_viewed_time) {
    return { icon: mailOpenOutline, label: 'Opened', color: 'var(--ion-color-success)' };
  }

  if (estimate.is_emailed) {
    return { icon: mailOutline, label: 'Sent', color: 'var(--ion-color-primary)' };
  }

  return null;
}

const EstimateList: React.FC<EstimateListProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const router = useIonRouter();
  const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
  const titleCardAnimationRef = useRef<HTMLIonCardElement>(null);
  const contentRef = useRef<HTMLIonGridElement>(null);

  const [estimates, setEstimates] = useState<ZohoEstimate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [isSearchClosing, setIsSearchClosing] = useState(false);
  const [isSearchHidden, setIsSearchHidden] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [selectedEstimateId, setSelectedEstimateId] = useState<string | null>(null);
  const filterTriggerRef = useRef<HTMLIonButtonElement>(null);
  const sortTriggerRef = useRef<HTMLIonButtonElement>(null);
  const pickerPortalRef = useRef<HTMLIonGridElement>(null);
  const searchRef = useRef<HTMLIonSearchbarElement | null>(null);
  const overlayRef = useRef<HTMLElement | null>(null);
  // Lift the floating search bar above the native keyboard (same as the other search bars)
  useKeyboardLift({ bars: () => [overlayRef.current] });

  const applyProgress = useCallback((progress: number, _smooth: boolean) => {
    baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
    if (titleCardAnimationRef.current) {
      titleCardAnimationRef.current.style.setProperty('--content-opacity', String(1 - progress));
      titleCardAnimationRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
    }
  }, []);

  const handleScroll = useScrollSnap(contentRef, 66, applyProgress);

  const loadEstimates = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const data = await getZohoEstimates();
      setEstimates(data);
    } catch (err) {
      console.error('[EstimateList] failed to load estimates', err);
      setError(t('pages.EstimateList.unableToLoadEstimatesRightNow'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    // Deferred via a resolved-promise microtask (not called synchronously
    // in the effect body) — loadEstimates's own setState calls are needed
    // on every real invocation (including the many other call sites), so
    // they can't just be dropped.
    Promise.resolve().then(() => loadEstimates());
  }, [loadEstimates]);

  const filteredEstimates = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    return estimates.filter((estimate) => {
      const status = String(estimate.status || '').toLowerCase();
      if (statusFilter !== 'all' && status !== statusFilter) return false;
      if (!needle) return true;

      return [
        estimate.estimate_number,
        estimate.customer_name,
        estimate.reference_number,
        estimate.status,
      ].some((value) => String(value || '').toLowerCase().includes(needle));
    });
  }, [estimates, searchText, statusFilter]);

  const sortedEstimates = useMemo(() => {
    const items = [...filteredEstimates];
    const direction = sortDirection === 'asc' ? 1 : -1;
    items.sort((a, b) => {
      switch (sortField) {
        case 'amount': {
          const amountA = Number(a.total || 0);
          const amountB = Number(b.total || 0);
          return (amountA - amountB) * direction;
        }
        case 'estimateNumber':
          return String(a.estimate_number || '').localeCompare(String(b.estimate_number || '')) * direction;
        case 'customer':
          return String(a.customer_name || '').localeCompare(String(b.customer_name || '')) * direction;
        case 'expiry':
          return String(a.expiry_date || a.due_date || '').localeCompare(String(b.expiry_date || b.due_date || '')) * direction;
        case 'date':
        default:
          return String(a.date || '').localeCompare(String(b.date || '')) * direction;
      }
    });
    return items;
  }, [filteredEstimates, sortDirection, sortField]);

  const handleRefresh = async (event: CustomEvent) => {
    await loadEstimates();
    event.detail.complete();
  };

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    } else if (router.canGoBack()) {
      router.goBack();
    } else {
      router.push('/tabs/manage');
    }
  }, [onClose, router]);

  const _totalValue = useMemo(
    () => sortedEstimates.reduce((sum, estimate) => sum + Number(estimate.total || 0), 0),
    [sortedEstimates]
  );

  const handleSort = useCallback((field: SortField) => {
    if (field === sortField) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortField(field);
    setSortDirection('desc');
  }, [sortField]);

  const openEstimatePreview = useCallback((estimate: ZohoEstimate) => {
    const estimateId = String(estimate.estimate_id || estimate.invoice_id || '').trim();
    if (!estimateId) return;
    if (onClose) {
      setSelectedEstimateId(estimateId);
      return;
    }
    router.push(`/tabs/estimate/${estimateId}`);
  }, [onClose, router]);

  const updateOverlayPosition = useCallback(() => {
    applySearchOverlayPosition(overlayRef.current, isSearchVisible);
  }, [isSearchVisible]);

  useEffect(() => {
    updateOverlayPosition();
    window.addEventListener('resize', updateOverlayPosition);
    return () => window.removeEventListener('resize', updateOverlayPosition);
  }, [updateOverlayPosition]);

  useEffect(() => {
    const id = window.setTimeout(() => updateOverlayPosition(), 30);
    return () => window.clearTimeout(id);
  }, [isSearchVisible, updateOverlayPosition]);

  const handleSearchToggle = useCallback(() => {
    if (isSearchVisible) {
      setSearchText('');
      setIsSearchClosing(true);
      setIsSearchVisible(false);
      setTimeout(() => {
        setIsSearchHidden(true);
        setIsSearchClosing(false);
      }, 220);
      return;
    }

    setIsSearchHidden(false);
    setIsSearchVisible(true);
    setTimeout(() => {
      searchRef.current?.setFocus();
    }, 40);
  }, [isSearchVisible]);

  const content = (
    <>
      <BasePageWithHeader
        ref={baseHeaderRef}
        title={t('pages.EstimateList.estimates')}
        toolbarClassName={styles.toolbar}
        smallTitleClassName={styles.smallTitle}
        titleHeaderClassName={styles.titleHeader}
        startButtons={
          <>
            <IonButton shape="round" className={styles.closeButton} onClick={handleClose}>
              <IonIcon icon={chevronBack} />
            </IonButton>
            <IonButton shape="round" className={styles.closeButton} style={{ visibility: 'hidden' }}>
              <IonIcon icon={chevronBack} />
            </IonButton>
          </>
        }
        endButtons={
          <>
            <IonButton shape="round" className={styles.newInvoiceBtn} onClick={() => setSelectedEstimateId('new')}>
              <IonIcon icon={addOutline} />
            </IonButton>
            <IonButton shape="round" className={styles.searchButton} onClick={handleSearchToggle}>
              <IonIcon icon={isSearchVisible ? close : searchIcon} />
            </IonButton>
          </>
        }
      >
        <IonContent scrollY={false} className={styles.content}>
          <PositionedRefresher
            titleRef={titleCardAnimationRef}
            contentRef={contentRef}
            offset={46}
            anchor="bottom"
            pullMin={120}
            pullMax={200}
            pullFactor={0.6}
            onIonRefresh={handleRefresh}
            style={{
              '--ion-color-step-250': 'var(--ion-background-color)',
              '--ion-color-step-200': 'var(--ion-background-color)',
              '--ion-background-color': 'transparent',
            } as React.CSSProperties}
          >
            <IonRefresherContent pullingIcon="bubbles" refreshingSpinner="bubbles" />
          </PositionedRefresher>

          <IonGrid
            ref={contentRef}
            className={styles.container}
            onScroll={(e) => handleScroll((e.target as HTMLElement).scrollTop || 0)}
          >
            <TitleCard
              ref={titleCardAnimationRef}
              title={t('pages.EstimateList.estimates')}
              cardClassName={styles.titleCard}
              titleContainerClassName={styles.titleContainer}
              titleClassName={styles.titleText}
            />

            <IonGrid className={styles.filterBar}>
              <IonButton
                ref={filterTriggerRef}
                fill="clear"
                className={styles.periodBtn}
                onClick={() => setShowFilterDropdown((current) => !current)}
              >
                {STATUS_OPTIONS.find((option) => option.value === statusFilter)?.label || t('pages.EstimateList.allEstimates')}
                <IonIcon icon={chevronDownOutline} />
              </IonButton>

              <IonButton
                ref={sortTriggerRef}
                fill="clear"
                className={`${styles.periodBtn} ${styles.sortTrigger}`}
                onClick={() => setShowSortDropdown((current) => !current)}
              >
                <IonIcon icon={swapVertical} />
              </IonButton>
            </IonGrid>

            <IonGrid ref={pickerPortalRef} className={styles.pickerPortalRoot} />

            <PortalDropdown
              isOpen={showFilterDropdown}
              anchorRef={filterTriggerRef}
              portalRef={pickerPortalRef}
              onClose={() => setShowFilterDropdown(false)}
              value={statusFilter}
              onSelect={(value) => {
                setStatusFilter(value as StatusFilter);
                setShowFilterDropdown(false);
              }}
              options={STATUS_OPTIONS.map((option) => ({ label: option.label, value: option.value }))}
            />

            <PortalDropdown
              isOpen={showSortDropdown}
              anchorRef={sortTriggerRef}
              portalRef={pickerPortalRef}
              onClose={() => setShowSortDropdown(false)}
              align="end"
              value={sortField}
              onSelect={(value) => {
                handleSort(value as SortField);
                setShowSortDropdown(false);
              }}
              options={SORT_OPTIONS.map((option) => ({
                label: option.label,
                value: option.value,
                sublabel: sortField === option.value ? (sortDirection === 'asc' ? '↑' : '↓') : undefined,
              }))}
            />

            {loading ? (
              <IonList className={styles.mobileList} lines="none" aria-hidden="true">
                {[0, 1, 2, 3, 4].map((i) => (
                  <IonCard key={`skeleton-${i}`} className={`${styles.card} ${styles.skeletonCard}`}>
                    <IonCardContent>
                      <IonItem lines="none" className={styles.cardTopItem}>
                        <IonSkeletonText animated slot="start" className={`${styles.skeletonBar} ${styles.cardAvatar}`} style={{ borderRadius: '50%' }} />
                        <IonLabel>
                          <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '55%', height: 16, margin: 0 }} />
                        </IonLabel>
                        <IonSkeletonText animated slot="end" className={styles.skeletonBar} style={{ width: 64, height: 20, margin: 0, borderRadius: 10 }} />
                      </IonItem>
                      <IonGrid className={styles.statsRow}>
                        <IonRow>
                          {[0, 1, 2].map((j) => (
                            <IonCol key={j} className={styles.statCol}>
                              <IonSkeletonText animated className={styles.skeletonBar} style={{ width: 16, height: 16, margin: 0, borderRadius: 4 }} />
                              <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '65%', height: 12, margin: '1px 0' }} />
                              <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '40%', height: 10, margin: '1px 0' }} />
                            </IonCol>
                          ))}
                        </IonRow>
                      </IonGrid>
                    </IonCardContent>
                  </IonCard>
                ))}
              </IonList>
            ) : error ? (
              <IonGrid className={styles.emptyState}>
                <IonIcon icon={refreshOutline} className={styles.stateIcon} />
                <h3>{t('pages.EstimateList.unableToLoadEstimates')}</h3>
                <p>{error}</p>
                <IonButton fill="clear" className={styles.stateAction} onClick={() => loadEstimates()}>{t('pages.EstimateList.tryAgain')}
                </IonButton>
              </IonGrid>
            ) : sortedEstimates.length === 0 ? (
              <IonGrid className={styles.emptyState}>
                <IonIcon icon={documentTextOutline} size="large" color="medium" />
                <h3>{t('pages.EstimateList.noEstimatesFound')}</h3>
                <p>{t('pages.EstimateList.tryAdjustingYourSearchOrFilters')}</p>
              </IonGrid>
            ) : (
              <>
                <IonList className={styles.mobileList} lines="none">
                  {sortedEstimates.map((estimate) => {
                    const viewedState = getViewedState(estimate);
                    return (
                      <IonCard
                        key={estimate.estimate_id || estimate.invoice_id}
                        className={styles.card}
                        button
                        onClick={() => openEstimatePreview(estimate)}
                      >
                        <IonCardContent>
                          <IonItem lines="none" className={styles.cardTopItem}>
                            <IonAvatar slot="start" className={styles.cardAvatar}>
                              <img
                                src={getDefaultAvatar(estimate.customer_name || '')}
                                alt={estimate.customer_name || ''}
                              />
                            </IonAvatar>
                            <IonLabel>
                              <h2 className={styles.cardName}>
                                {estimate.customer_name || t('pages.EstimateList.unknownCustomer')}
                                {viewedState && (
                                  <IonIcon
                                    icon={viewedState.icon}
                                    className={styles.viewedIcon}
                                    style={{ color: viewedState.color }}
                                    title={viewedState.label}
                                  />
                                )}
                              </h2>
                            </IonLabel>
                            <IonBadge className={`${styles.badge} ${getStatusTone(estimate.status || '')}`} slot="end">
                              {translateDynamic(estimate.status || 'draft')}
                            </IonBadge>
                          </IonItem>

                        <IonGrid className={styles.statsRow}>
                          <IonRow>
                            <IonCol className={styles.statCol}>
                              <IonIcon icon={cashOutline} className={`${styles.statIcon} ${styles.statIconGreen}`} />
                              <IonText>
                                <p className={styles.statValue}>
                                  {formatCurrency(Number(estimate.total || 0), estimate.currency_code || 'USD')}
                                </p>
                              </IonText>
                              <IonText>
                                <p className={styles.statMeta}>{t('pages.EstimateList.amount')}</p>
                              </IonText>
                            </IonCol>

                            <IonCol className={styles.statCol}>
                              <IonIcon icon={receiptOutline} className={`${styles.statIcon} ${styles.statIconBlue}`} />
                              <IonText>
                                <p className={styles.statValue}>{estimate.estimate_number || 'N/A'}</p>
                              </IonText>
                              <IonText>
                                <p className={styles.statMeta}>{t('pages.EstimateList.estimate')}</p>
                              </IonText>
                            </IonCol>

                            <IonCol className={`${styles.statCol} ${styles.statColLast}`}>
                              <IonIcon icon={calendarOutline} className={`${styles.statIcon} ${styles.statIconPurple}`} />
                              <IonText>
                                <p className={styles.statValue}>{formatDate(estimate.expiry_date || estimate.due_date)}</p>
                              </IonText>
                              <IonText>
                                <p className={styles.statMeta}>{t('pages.EstimateList.expires')}</p>
                              </IonText>
                            </IonCol>
                          </IonRow>
                        </IonGrid>

                          {estimate.reference_number && (
                            <IonText className={styles.referenceText}>{t('pages.EstimateList.ref', { reference_number: estimate.reference_number })}</IonText>
                          )}
                        </IonCardContent>
                      </IonCard>
                    );
                  })}
                </IonList>
              </>
            )}

            {isSearchVisible && <IonGrid className={styles.searchBarSpacer} />}
          </IonGrid>
        </IonContent>

        <IonFab
          ref={(element) => {
            overlayRef.current = element as unknown as HTMLElement;
          }}
          className={`${styles.searchMorph} ${isSearchVisible ? styles.searchMorphExpanded : ''} ${isSearchClosing ? styles.searchMorphClosing : ''} ${isSearchHidden ? styles.hidden : ''}`}
          aria-hidden={!isSearchVisible}
        >
          <IonSearchbar
            ref={searchRef}
            value={searchText}
            debounce={80}
            onIonInput={(e) => setSearchText(e.detail.value ?? '')}
            onIonClear={() => setSearchText('')}
            placeholder={t('pages.EstimateList.searchEstimates', { length: estimates.length })}
            animated
            showCancelButton="never"
            showClearButton={searchText ? 'always' : 'never'}
            className={`${styles.searchBar} ${styles.floatingSearchBarInline} ${isSearchVisible ? styles.floatingSearchBarInlineExpanded : ''}`}
          />
        </IonFab>
      </BasePageWithHeader>

      {selectedEstimateId !== null && (
        <IonModal
          isOpen={selectedEstimateId !== null}
          onDidDismiss={() => setSelectedEstimateId(null)}
          handle={false}
        >
          {selectedEstimateId === 'new' ? (
            <CreateInvoice
              onClose={() => setSelectedEstimateId(null)}
              onCreated={() => {
                setSelectedEstimateId(null);
                loadEstimates();
              }}
              prefill={{ invoiceType: 'QUOTE', lineItems: [] }}
            />
          ) : (
            <Invoice
              documentType="estimate"
              invoiceId={selectedEstimateId}
              onClose={() => setSelectedEstimateId(null)}
            />
          )}
        </IonModal>
      )}
    </>
  );

  return onClose
    ? <div className={styles.modalWrapper}>{content}</div>
    : <IonPage className={styles.page}>{content}</IonPage>;
};

export default EstimateList;